import { Kiosk } from "domains/kiosk/aggregates/Kiosk";
import React, {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import { useHistory } from "react-router-dom";
import { useCartStore } from "modules/cart/infra/store/CartStore";
import styles from "./IdleTime.module.scss";

const DELAY = 1000;
const NO_TIMER = 9999;
const EXPIRATION_DEFAULT_TIME = 30;

function useInterval(callback: () => void, delay: number) {
  const savedCallback = useRef<() => void>();

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    let id = setInterval(() => {
      savedCallback.current && savedCallback.current();
    }, delay);
    return () => clearInterval(id);
  }, [delay]);
}

interface IdleContext {
  resetCounter: () => void;
  handleActiveIdleTimer: () => void;
  handleActiveDefaultIdleTimer: () => void;
  handleDisableIdleTimer: () => void;
}

const IdleContext = createContext<IdleContext>({} as IdleContext);

interface IdleContextProviderProps {
  kiosk: Kiosk | null;
  children: ReactNode;
}

export const IdleProvider: FC<IdleContextProviderProps> = ({ children, kiosk }) => {
  const { replace } = useHistory();
  const [isActive, setIsActive] = useState(false);
  const expirationTime = useRef(kiosk?.expirationTime ?? EXPIRATION_DEFAULT_TIME);
  const counterRef = useRef<number>(expirationTime.current);
  const [renderCounter, setRenderCounter] = useState<number | "">("");

  const handleActiveIdleTimer = useCallback(() => {
    expirationTime.current = kiosk?.expirationTime ?? NO_TIMER;
    counterRef.current = expirationTime.current;
    setIsActive(true);
  }, [kiosk]);

  const handleActiveDefaultIdleTimer = useCallback(() => {
    expirationTime.current = kiosk?.expirationTime ?? EXPIRATION_DEFAULT_TIME;
    counterRef.current = expirationTime.current;
    setIsActive(true);
  }, [kiosk]);

  const handleDisableIdleTimer = useCallback(() => {
    setIsActive(false);
    setRenderCounter("");
  }, []);

  const resetCounter = useCallback(() => {
    counterRef.current = expirationTime.current;
    if (counterRef.current <= 10) {
      setRenderCounter(counterRef.current);
    } else {
      setRenderCounter("");
    }
  }, []);

  useInterval(() => {
    if (isActive) {
      counterRef.current -= 1;
      if (counterRef.current <= 10) {
        setRenderCounter(counterRef.current);
      }
      if (counterRef.current <= 0) {
        setIsActive(false);
        setRenderCounter("");
        // O modal de maioridade/CPF fica no store do carrinho e sobreviveria à troca de rota.
        const { setShowVerifyMajorityModal, clearTempMajorityProduct } = useCartStore.getState();
        setShowVerifyMajorityModal(false);
        clearTempMajorityProduct();
        replace("/");
      }
    }
  }, DELAY);

  useEffect(() => {
    // Captura: BottomModal e teclado virtual chamam stopPropagation e o clique não sobe até o window.
    window.addEventListener("click", resetCounter, true);
    return () => {
      window.removeEventListener("click", resetCounter, true);
    };
  }, [resetCounter]);

  return (
    <IdleContext.Provider
      value={{
        resetCounter,
        handleActiveDefaultIdleTimer,
        handleActiveIdleTimer,
        handleDisableIdleTimer,
      }}
    >
      {children}
      <div className={styles.IdleTimeContainer}>
        {isActive ? renderCounter : ""}
      </div>
    </IdleContext.Provider>
  );
};

export const useIdle = () => {
  const context = useContext(IdleContext);
  return context;
};
